import axios from "axios";
import api, { get, post, patch, del } from "./api";
import {
  VaultFile,
  VaultFolder,
  ShareLink,
  ActivityLog,
  PaginatedResponse,
  FileListParams,
  FolderListParams,
  CreateShareParams,
  UpdateFileParams,
  CreateFolderParams,
  UpdateFolderParams,
  FileAccessGrant,
  FolderAccessGrant,
  GrantAccessParams,
  UpdateAccessParams,
  SharedWithMeResponse,
  MeContext,
  GroupItemsResult,
} from "@/types";
import { API_ROUTES, DEFAULT_PAGE_SIZE } from "@/utils/constants";

// File operations
export async function listFiles(
  params: FileListParams = {}
): Promise<PaginatedResponse<VaultFile>> {
  const queryParams = new URLSearchParams();

  if (params.folderId !== undefined) {
    queryParams.set("folderId", params.folderId || "");
  }
  if (params.search) {
    queryParams.set("search", params.search);
  }
  if (params.sort) {
    queryParams.set("sort", params.sort);
  }
  if (params.order) {
    queryParams.set("order", params.order);
  }
  if (params.cursor) {
    queryParams.set("cursor", params.cursor);
  }
  if (params.starred !== undefined) {
    queryParams.set("starred", String(params.starred));
  }
  queryParams.set("limit", String(params.limit || DEFAULT_PAGE_SIZE));

  const query = queryParams.toString();
  // API returns { files: VaultFile[], nextCursor: string | null, total: number }
  // Remap to the internal PaginatedResponse shape used by hooks
  const response = await get<{ files: VaultFile[]; nextCursor: string | null; total: number }>(
    `${API_ROUTES.files}${query ? `?${query}` : ""}`
  );
  return {
    data: response.files || [],
    total: response.total || 0,
    continuationToken: response.nextCursor ?? null,
  };
}

export async function getFile(fileId: string): Promise<VaultFile> {
  return get<VaultFile>(API_ROUTES.file(fileId));
}

export async function updateFile(
  fileId: string,
  data: UpdateFileParams
): Promise<VaultFile> {
  return patch<VaultFile>(API_ROUTES.file(fileId), data);
}

export async function deleteFile(fileId: string): Promise<void> {
  return del<void>(API_ROUTES.file(fileId));
}

// List deleted files (history)
export async function listDeletedFiles(
  limit: number = 50
): Promise<{ data: VaultFile[]; total: number }> {
  return get<{ data: VaultFile[]; total: number }>(
    `${API_ROUTES.filesDeleted}?limit=${limit}`
  );
}

// Upload a file in a single multipart request; the API proxies it to blob
// storage and returns the file record already active (no confirm step).
export async function uploadFile(
  file: File,
  folderId: string | null,
  tags: string[] = [],
  onProgress?: (progress: number) => void
): Promise<VaultFile> {
  const form = new FormData();
  form.append("file", file, file.name);
  if (folderId) {
    form.append("folderId", folderId);
  }
  if (tags.length > 0) {
    form.append("tags", JSON.stringify(tags));
  }

  try {
    return await post<VaultFile>(API_ROUTES.fileUpload, form, {
      // Clear the instance default (application/json) so the browser sets
      // multipart/form-data with the boundary.
      headers: { "Content-Type": undefined as unknown as string },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          onProgress(
            Math.round((progressEvent.loaded * 100) / progressEvent.total)
          );
        }
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      error.message = error.response.data.message;
    }
    throw error;
  }
}

function filenameFromContentDisposition(
  header: string | undefined
): string | null {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      // malformed encoding — fall through to the plain filename
    }
  }
  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1].trim() : null;
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

// With responseType "blob", API error bodies arrive as JSON inside a Blob;
// replace them with the parsed object so callers can keep reading
// error.response.data.message / error.message.
async function rethrowBlobError(error: unknown): Promise<never> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const body = JSON.parse(await error.response.data.text());
      error.response.data = body;
      error.message = body.message || body.error || error.message;
    } catch {
      // body was not JSON — keep the original error
    }
  }
  throw error;
}

/**
 * Downloads a file by streaming it through the share API.
 *
 * `GET /files/{fileId}/download` authorizes the owner or any user holding an
 * active file/folder access grant, so this is the single download path for
 * all authenticated surfaces.
 *
 * @param fileId   - UUID of the file
 * @param fileName - Fallback name when Content-Disposition is not exposed
 */
export async function downloadFile(
  fileId: string,
  fileName: string
): Promise<void> {
  try {
    const response = await api.get(API_ROUTES.fileDownload(fileId), {
      responseType: "blob",
    });
    const headerName = filenameFromContentDisposition(
      response.headers["content-disposition"]
    );
    triggerBlobDownload(response.data as Blob, headerName || fileName);
  } catch (error) {
    return rethrowBlobError(error);
  }
}

// Share operations
export async function createShare(
  params: CreateShareParams
): Promise<ShareLink> {
  return post<ShareLink>(API_ROUTES.shares, params);
}

export async function listShares(fileId?: string): Promise<ShareLink[]> {
  const queryParams = new URLSearchParams();
  if (fileId) {
    queryParams.set("fileId", fileId);
  }
  const query = queryParams.toString();
  return get<ShareLink[]>(`${API_ROUTES.shares}${query ? `?${query}` : ""}`);
}

export async function getShare(shareId: string): Promise<ShareLink> {
  return get<ShareLink>(API_ROUTES.share(shareId));
}

export async function deleteShare(shareId: string): Promise<void> {
  return del<void>(API_ROUTES.share(shareId));
}

// Public share access (no authentication required)
export interface PublicShareInfo {
  valid: boolean;
  requiresPassword?: boolean;
  permissions?: "view" | "download";
  expiresAt?: string;
  fileName?: string;
  mimeType?: string;
  size?: number;
}

export async function getPublicShare(shareToken: string): Promise<PublicShareInfo> {
  // Anonymous endpoint — bare axios so the MSAL interceptor never runs
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
  const response = await axios.get<{
    success: boolean;
    message?: string;
    data: PublicShareInfo;
  }>(`${baseUrl}${API_ROUTES.publicShare(shareToken)}`);
  return response.data.data;
}

/**
 * Downloads a publicly shared file by streaming it through the share API.
 *
 * The password (when the share has one) travels in this same request; there
 * is no separate verification step. The body must be omitted entirely for
 * shares without a password.
 *
 * Errors: 403 ACCESS_DENIED (wrong password), 403 DOWNLOAD_NOT_PERMITTED
 * (view-only share), 401 (invalid/expired token), 404 (share or file gone) —
 * rethrown with the JSON body parsed out of the error Blob.
 *
 * @param shareToken       - Public share token from the URL
 * @param password         - Share password, only when the share requires one
 * @param fallbackFileName - Name used when Content-Disposition is not exposed
 */
export async function downloadPublicShare(
  shareToken: string,
  password?: string,
  fallbackFileName?: string
): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
  try {
    const response = await axios.post(
      `${baseUrl}${API_ROUTES.publicShareDownload(shareToken)}`,
      password ? { password } : undefined,
      { responseType: "blob" }
    );
    const headerName = filenameFromContentDisposition(
      response.headers["content-disposition"]
    );
    triggerBlobDownload(
      response.data as Blob,
      headerName || fallbackFileName || "download"
    );
  } catch (error) {
    return rethrowBlobError(error);
  }
}

// Access Grant operations (user-based sharing)
export async function grantAccess(
  fileId: string,
  params: GrantAccessParams
): Promise<FileAccessGrant> {
  return post<FileAccessGrant>(`/files/${fileId}/access/grant`, params);
}

export async function listAccessGrants(
  fileId: string,
  includeExpired = false,
  includeInactive = false
): Promise<FileAccessGrant[]> {
  const queryParams = new URLSearchParams();
  if (includeExpired) {
    queryParams.set("includeExpired", "true");
  }
  if (includeInactive) {
    queryParams.set("includeInactive", "true");
  }
  const query = queryParams.toString();
  return get<FileAccessGrant[]>(
    `/files/${fileId}/access${query ? `?${query}` : ""}`
  );
}

export async function updateAccessGrant(
  fileId: string,
  grantId: string,
  params: UpdateAccessParams
): Promise<FileAccessGrant> {
  return patch<FileAccessGrant>(`/files/${fileId}/access/${grantId}`, params);
}

export async function revokeAccess(
  fileId: string,
  grantId: string
): Promise<void> {
  return del<void>(`/files/${fileId}/access/${grantId}`);
}

export async function listSharedWithMe(): Promise<SharedWithMeResponse> {
  return get<SharedWithMeResponse>("/access/shared-with-me");
}

// Supervisor / group-visibility operations
export async function getMe(): Promise<MeContext> {
  return get<MeContext>("/me");
}

export async function getGroupItems(): Promise<GroupItemsResult> {
  return get<GroupItemsResult>("/access/group-items");
}

// Fetch a file's binary content (auth-injected) for preview/download.
// Used by the supervision view, where the supervisor isn't the owner.
export async function fetchFileBlob(fileId: string): Promise<Blob> {
  const response = await api.get(`/files/${fileId}/download`, {
    responseType: "blob",
  });
  return response.data as Blob;
}

// Folder access grant operations
export async function grantFolderAccess(
  folderId: string,
  params: GrantAccessParams
): Promise<FolderAccessGrant> {
  return post<FolderAccessGrant>(`/folders/${folderId}/access/grant`, params);
}

export async function revokeFolderAccess(
  folderId: string,
  grantId: string
): Promise<void> {
  return del<void>(`/folders/${folderId}/access/${grantId}`);
}

export interface SharedFolderFilesResponse {
  files: VaultFile[];
  total?: number;
}

export async function listSharedFolderFiles(folderId: string): Promise<SharedFolderFilesResponse> {
  return get<SharedFolderFilesResponse>(`/folders/${folderId}/files`);
}

export async function markFileDownloaded(folderId: string, fileId: string): Promise<void> {
  return post<void>(`/folders/${folderId}/files/${fileId}/mark-downloaded`);
}

// Folder operations
export async function listFolders(
  params: FolderListParams = {}
): Promise<VaultFolder[]> {
  const queryParams = new URLSearchParams();

  if (params.parentId !== undefined) {
    queryParams.set("parentId", params.parentId || "");
  }

  const query = queryParams.toString();
  return get<VaultFolder[]>(
    `${API_ROUTES.folders}${query ? `?${query}` : ""}`
  );
}

export async function getFolder(folderId: string): Promise<VaultFolder> {
  return get<VaultFolder>(API_ROUTES.folder(folderId));
}

export async function createFolder(params: CreateFolderParams): Promise<VaultFolder> {
  return post<VaultFolder>(API_ROUTES.folders, params);
}

export async function updateFolder(
  folderId: string,
  params: UpdateFolderParams
): Promise<VaultFolder> {
  return patch<VaultFolder>(API_ROUTES.folder(folderId), params);
}

export async function deleteFolder(folderId: string): Promise<void> {
  return del<void>(API_ROUTES.folder(folderId));
}

// Activity operations
export async function listActivity(
  resourceId?: string,
  limit: number = 20
): Promise<ActivityLog[]> {
  const queryParams = new URLSearchParams();
  if (resourceId) {
    queryParams.set("resourceId", resourceId);
  }
  queryParams.set("limit", String(limit));

  const query = queryParams.toString();
  return get<ActivityLog[]>(`${API_ROUTES.activity}${query ? `?${query}` : ""}`);
}

// User management operations (Azure AD B2B)
export interface CheckUserResponse {
  email: string;
  exists: boolean;
  canInvite: boolean;
}

export interface InviteUserRequest {
  email: string;
  displayName?: string;
  sendInvitationMessage?: boolean;
  customMessage?: string;
  inviteRedirectUrl?: string;
}

export interface InviteUserResponse {
  userId: string;
  userPrincipalName: string;
  displayName: string;
  inviteRedeemUrl: string;
  status: string;
}

export async function checkUser(email: string): Promise<CheckUserResponse> {
  return post<CheckUserResponse>("/users/check", { email });
}

export async function inviteGuestUser(params: InviteUserRequest): Promise<InviteUserResponse> {
  return post<InviteUserResponse>("/users/invite", params);
}
